import { formatIst } from '@wa/shared';

/**
 * `wide` is for screens whose content is a data table rather than a form.
 *
 * The default 6xl is right for reading — a settings form or a list of stats set in a
 * 1600px-wide column is worse, not better. But Channels' table needs 1139px and this
 * container gives it 1104, so on every desktop the last two columns sat outside the
 * table's scroller with 450px of empty margin beside them. Nothing said they were
 * there: the page did not scroll sideways, and the cells were in the DOM to be read
 * by anything that asked.
 *
 * An opt-in rather than a change to the default, because it is a claim about one
 * screen's content, and the screens that read better narrow should stay narrow.
 */
export function Page({ title, subtitle, wide = false, children }: {
  title: string; subtitle?: string; wide?: boolean; children: React.ReactNode;
}) {
  return (
    <div className={`mx-auto px-6 py-8 ${wide ? 'max-w-[92rem]' : 'max-w-6xl'}`}>
      <h1 className="text-title font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="mt-1 text-body text-(--color-muted)">{subtitle}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

// DataTable, Cell and Empty moved to @wa/shared when the bot screens became shared
// between the two portals. Re-exported so every existing import keeps working, and
// so there is still one definition rather than two that drift.
export { DataTable, Cell, Empty } from '@wa/shared';

export function Stat({ label, value, tone = 'ink' }: {
  label: string; value: number | string; tone?: 'ink' | 'good' | 'warn' | 'bad';
}) {
  const toneClass = {
    ink: 'text-(--color-ink)',
    good: 'text-(--color-good)',
    warn: 'text-(--color-warn)',
    bad: 'text-(--color-bad)',
  }[tone];

  return (
    <div className="rounded border border-(--color-line) bg-(--color-surface) px-4 py-3.5">
      <div className={`text-display font-semibold tabular-nums ${toneClass}`}>{value}</div>
      <div className="mt-0.5 text-small text-(--color-muted)">{label}</div>
    </div>
  );
}

/**
 * A timestamp, in IST.
 *
 * This was `toLocaleString()`, which renders in the *browser's* locale and timezone.
 * On a machine set to anything but Asia/Kolkata it silently reported the wrong hour,
 * and on an en-US machine it wrote 2/11/2026 for the eleventh of February. Every
 * other screen in the product goes through the shared IST formatter; the admin
 * portal was simply never swept.
 */
export function when(value: string | null): string {
  return formatIst(value);
}
